import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getQuoteByCode } from "@/integrations/supabase/quotes";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, Download, Eye, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/components/AuthProvider";
import { useInternationalization } from "@/contexts/InternationalizationContext";
import { useState, useEffect } from "react";
import { fixEncoding } from "@/utils/fixEncoding";
import { isoDateToBR } from "@/utils/dateOnly";
import { useOnboardingPDFDetection, PDF_HIGHLIGHT_CLASS } from "@/components/OnboardingPDFHighlight";
import { getOnboardingProgress, completeOnboardingStep } from "@/integrations/supabase/onboarding";
import { ONBOARDING_STEPS } from "@/constants/onboarding";
import { toast } from "sonner";
import ArquivosSection from "@/components/ArquivosSection";
import { getArquivos, ArquivoData } from "@/integrations/supabase/arquivos";

// Converter camelCase para formato legível: tipoAbertura → Tipo Abertura
function formatFieldName(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Adiciona espaço antes de cada maiúscula
    .replace(/^./, str => str.toUpperCase()) // Primeira letra maiúscula
    .trim();
}

export default function VisualizarOrcamento() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const { formatCurrency } = useInternationalization();
  const queryClient = useQueryClient();
  const [imageError, setImageError] = useState(false);
  const [isMarkingPDF, setIsMarkingPDF] = useState(false);
  const [arquivos, setArquivos] = useState<ArquivoData[]>([]);
  const [isLoadingArquivos, setIsLoadingArquivos] = useState(false);
  const { isPDFPending } = useOnboardingPDFDetection();

  const { data: onboardingProgress } = useQuery({
    queryKey: ["onboarding-progress", empresa?.id],
    queryFn: () => getOnboardingProgress(empresa?.id!),
    enabled: !!empresa?.id,
  });

  const { data: quoteData, isLoading, error } = useQuery({
    queryKey: ["quoteView", id],
    queryFn: async () => {
      if (!id) return null;
      const result = await getQuoteByCode(id);
      return result;
    },
    enabled: !!id,
  });

  // Carregar arquivos quando o orçamento é carregado
  useEffect(() => {
    if (isLoading || !quoteData?.quote?.id) return;

    const loadArquivos = async () => {
      setIsLoadingArquivos(true);
      try {
        const arquivosCarregados = await getArquivos('quotes', quoteData.quote.id);
        setArquivos(arquivosCarregados);
      } catch (error) {
        console.error("Erro ao carregar arquivos:", error);
      } finally {
        setIsLoadingArquivos(false);
      }
    };

    loadArquivos();
  }, [quoteData?.quote?.id, isLoading]);

  const handlePrintClick = async () => {
    if (!empresa?.id) {
      toast.error("Empresa não identificada");
      return;
    }

    // Marcar como concluído
    try {
      setIsMarkingPDF(true);
      console.log("🔄 Marcando etapa 4 como concluída...", {
        empresaId: empresa.id,
        step: ONBOARDING_STEPS.QUOTE_PDF_VIEWED,
      });

      const success = await completeOnboardingStep(
        empresa.id,
        ONBOARDING_STEPS.QUOTE_PDF_VIEWED
      );

      if (success) {
        console.log("✅ Etapa 4 marcada com sucesso!");
        await queryClient.invalidateQueries({
          queryKey: ['onboarding-progress', empresa.id]
        });
        toast.success("✅ PDF visualizado! Etapa 4 concluída!");
      } else {
        console.warn("⚠️ Falha ao marcar etapa 4");
        toast.error("Erro ao marcar etapa como concluída");
      }
    } catch (error) {
      console.error("❌ Erro ao marcar etapa 4:", error);
      toast.error("Erro ao processar");
    } finally {
      setIsMarkingPDF(false);
    }

    // Navegar para página de impressão
    navigate(`/orcamentos/${id}/impressao`);
  };

  // Extrair URL do arquivo das observações
  const extractFileUrl = (observations?: string | null): string | null => {
    if (!observations) return null;
    
    // Procurar por padrões como "Arquivo/Arte: URL" ou "Arquivo: URL"
    const patterns = [
      /Arquivo\/Arte:\s*(https?:\/\/[^\s\n]+)/i,
      /Arquivo:\s*(https?:\/\/[^\s\n]+)/i,
      /file_url:\s*(https?:\/\/[^\s\n]+)/i,
      /(https?:\/\/[^\s\/]+\.supabase\.co\/storage\/[^\s\n]+)/i,
    ];
    
    for (const pattern of patterns) {
      const match = observations.match(pattern);
      if (match && match[1]) {
        return match[1].trim();
      }
    }
    
    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error || !quoteData || !quoteData.quote) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/orcamentos")}
              className="border-gray-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>
        </header>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Orçamento não encontrado</h2>
            <p className="text-gray-600 mb-4">O orçamento solicitado não foi encontrado.</p>
            <Button onClick={() => navigate("/orcamentos")}>
              Voltar para Orçamentos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const quote = quoteData.quote;
  const items = quoteData.items || [];

  const itemsWithSpecs = items.filter(item => item?.especificacoes);
  console.log("🔍 Items with especificacoes:", itemsWithSpecs);

  // Remover personalizações duplicadas
  const removeDuplicatePersonalizations = (personalizations: typeof quoteData.personalizations = []) => {
    if (!personalizations || personalizations.length === 0) return [];
    
    // Sempre usar conteúdo para verificar duplicatas, mesmo se houver ID
    // Isso garante que personalizações idênticas sejam removidas mesmo com IDs diferentes
    const seenKeys = new Map<string, typeof personalizations[0]>();
    const uniqueItems: typeof personalizations = [];
    
    personalizations.forEach((item) => {
      // Criar chave baseada no conteúdo (normalizar para comparação)
      const personName = (item.person_name || '').trim().toLowerCase();
      const size = (item.size || '').trim().toLowerCase();
      const quantity = item.quantity || 1;
      const notes = (item.notes || '').trim().toLowerCase();
      
      const key = `${personName}_${size}_${quantity}_${notes}`;
      
      if (!seenKeys.has(key)) {
        seenKeys.set(key, item);
        uniqueItems.push(item);
      } else {
        console.warn(`Personalização duplicada removida:`, {
          person_name: item.person_name,
          size: item.size,
          quantity: item.quantity,
          notes: item.notes,
          id: item.id
        });
      }
    });
    
    if (personalizations.length !== uniqueItems.length) {
      console.log(`✅ Removidas ${personalizations.length - uniqueItems.length} personalizações duplicadas do orçamento. Original: ${personalizations.length}, Único: ${uniqueItems.length}`);
    }
    
    return uniqueItems;
  };
  
  const personalizations = removeDuplicatePersonalizations(quoteData.personalizations || []);
  const fileUrl = extractFileUrl(quote.observations);

  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unit_value), 0);
  const discount = (quote as any)?.discount || 0;
  const discountType = (quote as any)?.discount_type || 'fixed';
  const discountAmount = discountType === 'percentage'
    ? (subtotal * discount / 100)
    : discount;
  const total = Math.max(0, subtotal - discountAmount);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/orcamentos")}
                className="border-gray-300"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Orçamento - {quote.code}
                </h1>
                <p className="text-gray-600 text-sm">
                  Cliente: {quote.customer_name} | {quote.customer_phone}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={handlePrintClick}
                disabled={isMarkingPDF}
                className={`px-6 py-2 bg-blue-600 hover:bg-blue-700 ${isPDFPending ? PDF_HIGHLIGHT_CLASS : ""}`}
              >
                <Printer className="w-4 h-4 mr-2" />
                {isMarkingPDF ? "Marcando..." : "Imprimir"}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="grid gap-6">
          {/* Card Informativo de Onboarding - Etapa 4 */}
          {onboardingProgress?.shouldShowChecklist && onboardingProgress?.steps?.some(s => s.step === ONBOARDING_STEPS.QUOTE_PDF_VIEWED && !s.completed) && (
            <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <Eye className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-blue-900 mb-1">📋 Etapa 4 de 5: Veja como seu cliente receberá o orçamento</h3>
                    <p className="text-sm text-blue-800 mb-3">
                      Clique no botão "Imprimir" para visualizar exatamente como seu cliente receberá este orçamento. Assim você garante que tudo está apresentável!
                    </p>
                    <Button
                      onClick={handlePrintClick}
                      disabled={isMarkingPDF}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      {isMarkingPDF ? "Marcando..." : "Visualizar impressão"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Informações do Orçamento */}
          <Card>
            <CardHeader>
              <CardTitle>Informações do Orçamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Código</p>
                  <p className="font-semibold">{quote.code}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Data</p>
                  <p>{isoDateToBR(quote.date) || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cliente</p>
                  <p className="font-semibold">{quote.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Telefone</p>
                  <p>{quote.customer_phone || 'Não informado'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Arquivo / Arte - Seção melhorada com suporte a múltiplos uploads */}
          {quoteData?.quote?.id && (
            <ArquivosSection
              tableType="quotes"
              recordId={quoteData.quote.id}
              arquivos={arquivos}
              onArquivosUpdated={setArquivos}
              isExpanded={arquivos.length > 0}
            />
          )}

          {/* Produtos */}
          <Card>
            <CardHeader>
              <CardTitle>Produtos e Serviços</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Item</th>
                      <th className="text-left py-2 px-3">Descrição</th>
                      <th className="text-right py-2 px-3">Quantidade</th>
                      <th className="text-right py-2 px-3">Valor Unit.</th>
                      <th className="text-right py-2 px-3">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.id || index} className="border-b">
                        <td className="py-2 px-3">{index + 1}</td>
                        <td className="py-2 px-3">{fixEncoding(item.description)}</td>
                        <td className="py-2 px-3 text-right">{item.quantity}</td>
                        <td className="py-2 px-3 text-right">{formatCurrency(item.unit_value)}</td>
                        <td className="py-2 px-3 text-right font-semibold">
                          {formatCurrency(item.quantity * item.unit_value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted">
                      <td colSpan={4} className="py-3 px-3 text-right font-semibold">
                        Subtotal
                      </td>
                      <td className="py-3 px-3 text-right font-bold">
                        {formatCurrency(subtotal)}
                      </td>
                    </tr>
                    {discount > 0 && (
                      <tr className="bg-red-50">
                        <td colSpan={4} className="py-3 px-3 text-right font-semibold text-red-700">
                          Desconto ({discountType === 'percentage' ? `${discount.toFixed(2)}%` : '— Fixo'})
                        </td>
                        <td className="py-3 px-3 text-right font-bold text-red-700">
                          -{formatCurrency(discountAmount)}
                        </td>
                      </tr>
                    )}
                    <tr className="bg-primary/10">
                      <td colSpan={4} className="py-3 px-3 text-right font-semibold text-primary">
                        Valor Final
                      </td>
                      <td className="py-3 px-3 text-right font-bold text-lg text-primary">
                        {formatCurrency(total)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Especificações */}
          {itemsWithSpecs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Especificações dos Produtos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {itemsWithSpecs.map((item, index) => (
                      <div
                        key={item.id || index}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-semibold text-gray-900">
                              {fixEncoding(item.description)}
                            </p>
                            {item.category && (
                              <p className="text-sm text-gray-600">
                                Categoria: <span className="font-medium">{item.category}</span>
                              </p>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">Qtd: {item.quantity}</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(item.especificacoes)
                            .filter(([key, value]) => {
                              // Mostrar tipoCortinaId se for customizado
                              if (key.toLowerCase() === 'tipocortinaid') {
                                return String(value).startsWith('custom:');
                              }
                              // Não mostrar outros IDs
                              return !key.toLowerCase().includes('id');
                            })
                            .map(([key, value]) => {
                              let displayKey = formatFieldName(key);
                              let displayValue = String(value);

                              // Se for tipo customizado, extrair nome
                              if (key.toLowerCase() === 'tipocortinaid' && displayValue.startsWith('custom:')) {
                                displayKey = 'Tipo de Cortina';
                                displayValue = displayValue.replace('custom:', '');
                              }

                              return (
                                <div key={key} className="bg-white p-2 rounded border border-gray-100">
                                  <p className="text-xs font-semibold text-gray-700">
                                    {displayKey}
                                  </p>
                                  <p className="text-sm text-gray-900 mt-1">{displayValue}</p>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personalizações */}
          {personalizations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Personalizações</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Nome</th>
                        <th className="text-center py-2 px-3">Tamanho</th>
                        <th className="text-right py-2 px-3">Quantidade</th>
                        <th className="text-left py-2 px-3">Observações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {personalizations.map((item, index) => (
                        <tr key={item.id || index} className="border-b">
                          <td className="py-2 px-3 font-medium">{item.person_name}</td>
                          <td className="py-2 px-3 text-center">{item.size || "—"}</td>
                          <td className="py-2 px-3 text-right">{item.quantity}</td>
                          <td className="py-2 px-3">{item.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Observações */}
          {quote.observations && (
            <Card>
              <CardHeader>
                <CardTitle>Observações</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm">
                  {quote.observations.replace(/Arquivo\/Arte:\s*https?:\/\/[^\s\n]+/gi, '').trim()}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

